import { ReactNode } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export interface LightboxImage {
  url: string;
  alt?: string;
}

interface LightboxProps {
  images: LightboxImage[];
  currentIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  header?: ReactNode;
}

export function Lightbox({
  images,
  currentIndex,
  onClose,
  onPrev,
  onNext,
  header,
}: LightboxProps) {
  const currentImage = images[currentIndex];
  if (!currentImage) return null;

  const showNav = images.length > 1;

  return createPortal(
    <div className="fixed inset-4 z-[9999] flex items-center justify-center rounded-2xl bg-zinc-700/95">
      {header && (
        <div className="absolute left-4 top-4 flex items-center gap-2 text-white">
          {header}
        </div>
      )}

      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
      >
        <X className="h-6 w-6" />
      </button>

      {showNav && (
        <button
          type="button"
          onClick={onPrev}
          className="absolute left-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-gray-700 shadow-md transition-colors hover:bg-white"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}

      <div className="max-h-[80vh] max-w-[85vw] overflow-hidden rounded-xl">
        <img
          src={currentImage.url}
          alt={currentImage.alt ?? `Photo ${currentIndex + 1}`}
          className="max-h-[80vh] max-w-[85vw] object-contain"
        />
      </div>

      {showNav && (
        <button
          type="button"
          onClick={onNext}
          className="absolute right-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-gray-700 shadow-md transition-colors hover:bg-white"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}

      {showNav && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-sm text-white">
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>,
    document.body
  );
}
